import { Link } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Clock,
  Sparkles,
  Star,
  PlaneTakeoff,
  Compass,
  MapPin,
  Quote,
  CheckCircle2,
} from "lucide-react";
import { usePageSeo } from "@/hooks/usePageSeo";
import { useHeroSlides } from "@/hooks/useHeroSlides";
import { useEffect, useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import BookingWidget from "@/components/BookingWidget";
import heroImg from "@/assets/hero-caribbean.jpg";
import transferImg from "@/assets/service-transfer.jpg";
import toursImg from "@/assets/service-tours.jpg";
import saonaImg from "@/assets/destination-saona.jpg";
import puntaCanaImg from "@/assets/destination-puntacana.jpg";
import santoDomingoImg from "@/assets/destination-santodomingo.jpg";

const features = [
  {
    icon: ShieldCheck,
    title: "Trusted Drivers",
    desc: "Vetted, English-speaking chauffeurs who know every corner of the island.",
  },
  {
    icon: Clock,
    title: "On-Time, Always",
    desc: "Real-time flight tracking and 24/7 dispatch. We meet you when you arrive.",
  },
  {
    icon: Sparkles,
    title: "Premium Comfort",
    desc: "Late-model SUVs and spotless vans, sanitized between every ride.",
  },
  {
    icon: Star,
    title: "5-Star Service",
    desc: "Hundreds of reviews from travelers who returned home raving about us.",
  },
];

const destinations = [
  {
    name: "Saona Island",
    region: "La Romana",
    image: saonaImg,
    description: "A hidden Caribbean paradise of turquoise water and white sand.",
  },
  {
    name: "Punta Cana",
    region: "East Coast",
    image: puntaCanaImg,
    description: "Endless beaches, world-class resorts, and unforgettable sunsets.",
  },
  {
    name: "Santo Domingo",
    region: "Capital",
    image: santoDomingoImg,
    description: "Colonial history, vibrant streets, and the soul of the island.",
  },
];

const testimonials = [
  {
    name: "Sarah & James",
    place: "London, UK",
    text: "From the airport pickup to our Saona day trip, every detail was perfect. Allen Travels made our honeymoon truly unforgettable.",
    rating: 5,
  },
  {
    name: "Marcus Reed",
    place: "Toronto, CA",
    text: "Punctual, professional, and the SUV was immaculate. I won't use anyone else for transfers in DR again.",
    rating: 5,
  },
  {
    name: "Elena Rossi",
    place: "Milan, IT",
    text: "Booking was effortless and our guide was warm and knowledgeable. The whole experience felt premium without being stiff.",
    rating: 5,
  },
];

const stats = [
  { value: "12k+", label: "Happy travelers" },
  { value: "4.9", label: "Average rating" },
  { value: "30+", label: "Destinations" },
  { value: "24/7", label: "Concierge" },
];

const Index = () => {
  usePageSeo("home");
  const { slides } = useHeroSlides();
  const [activeSlide, setActiveSlide] = useState(0);

  const heroImages = slides.length > 0 ? slides.map((s) => s.image_url) : [heroImg];

  useEffect(() => {
    if (heroImages.length <= 1) return;
    const id = setInterval(() => {
      setActiveSlide((i) => (i + 1) % heroImages.length);
    }, 6000);
    return () => clearInterval(id);
  }, [heroImages.length]);

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-center pt-20 isolate overflow-hidden">
        <div className="absolute inset-0 z-0">
          {heroImages.map((src, i) => (
            <img
              key={src + i}
              src={src}
              alt="Caribbean coastline in the Dominican Republic"
              className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${
                i === activeSlide ? "opacity-100" : "opacity-0"
              }`}
              width={1920}
              height={1080}
              loading={i === 0 ? "eager" : "lazy"}
            />
          ))}
          <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--hero-bg))]/85 via-[hsl(var(--hero-bg))]/55 to-[hsl(var(--hero-bg))]/85" />
          <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--hero-bg))]/70 via-transparent to-transparent" />
          {heroImages.length > 1 && (
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2 z-10">
              {heroImages.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveSlide(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${
                    i === activeSlide ? "w-8 bg-white" : "w-4 bg-white/40 hover:bg-white/60"
                  }`}
                />
              ))}
            </div>
          )}
        </div>

        <div className="relative z-10 container mx-auto px-4 py-20 lg:py-28 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 text-white animate-fade-up">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full glass-dark text-xs font-medium tracking-wider uppercase">
              <Sparkles className="w-3.5 h-3.5 text-primary-glow" />
              Premium travel — Dominican Republic
            </span>
            <h1 className="font-display font-semibold text-5xl md:text-6xl lg:text-7xl leading-[1.05] mt-6">
              The Caribbean,
              <br />
              <span className="italic text-gradient-ocean">effortlessly yours.</span>
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/80 max-w-xl leading-relaxed">
              Private transfers and curated island tours, designed for travelers
              who want every moment to feel easy, elegant, and unforgettable.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/transfers"
                className="group inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-ocean text-primary-foreground font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth"
              >
                Book a transfer
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/tours"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/30 text-white font-semibold hover:bg-white/10 transition-smooth"
              >
                Explore tours
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-6 text-sm text-white/75">
              <div className="flex -space-x-2">
                {[1, 2, 3, 4].map((i) => (
                  <div
                    key={i}
                    className="w-9 h-9 rounded-full border-2 border-white/40 bg-gradient-ocean"
                    aria-hidden
                  />
                ))}
              </div>
              <div>
                <div className="flex items-center gap-1 text-accent">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Star key={i} className="w-4 h-4 fill-current" />
                  ))}
                </div>
                <span className="text-white/70">Loved by 12,000+ travelers</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="glass rounded-3xl p-2 shadow-elegant">
              <div className="rounded-2xl bg-card p-1">
                <BookingWidget />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      <section className="border-y border-border/60 bg-gradient-soft">
        <div className="container mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="font-display text-3xl md:text-4xl font-semibold text-gradient-ocean">
                {s.value}
              </div>
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="container mx-auto px-4 py-24">
        <div className="max-w-2xl mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">
            What we do
          </span>
          <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
            Two ways to experience the island.
          </h2>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {[
            {
              tag: "Transfers",
              title: "Private Airport & Resort Transfers",
              text: "Skip the chaos. We're tracking your flight and waiting with a name sign and chilled water — door to door, anywhere on the island.",
              image: transferImg,
              href: "/transfers",
              icon: PlaneTakeoff,
            },
            {
              tag: "Tours",
              title: "Curated Island Tours & Excursions",
              text: "Saona Island, Samaná, Catalina, ATV adventures, catamaran days. Small groups, English-speaking guides, no rush.",
              image: toursImg,
              href: "/tours",
              icon: Compass,
            },
          ].map((s) => (
            <Link
              key={s.tag}
              to={s.href}
              className="group relative overflow-hidden rounded-3xl bg-card shadow-card hover:shadow-elegant transition-smooth"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={s.image}
                  alt={s.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/85 via-[hsl(var(--hero-bg))]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-8 text-white">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full glass-dark text-[10px] uppercase tracking-[0.2em]">
                  <s.icon className="w-3 h-3" />
                  {s.tag}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mt-4">
                  {s.title}
                </h3>
                <p className="mt-2 text-white/80 max-w-md">{s.text}</p>
                <div className="mt-5 inline-flex items-center gap-2 text-primary-glow font-medium">
                  Discover more
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* WHY US */}
      <section className="bg-gradient-soft">
        <div className="container mx-auto px-4 py-24">
          <div className="grid lg:grid-cols-12 gap-12 items-start">
            <div className="lg:col-span-5">
              <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">
                Why Allen Travels
              </span>
              <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
                The details others <span className="italic text-gradient-ocean">skip.</span>
              </h2>
              <p className="mt-5 text-muted-foreground text-lg leading-relaxed">
                We treat every transfer and every tour like it's our own family on board. That's the difference you feel from the first message to the last goodbye.
              </p>

              <ul className="mt-8 space-y-3">
                {[
                  "Free cancellation up to 24 hours before",
                  "Bilingual concierge available 24/7",
                  "Child seats and accessibility on request",
                  "Transparent pricing — no surprises at the end",
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-foreground/85">
                    <CheckCircle2 className="w-5 h-5 text-primary mt-0.5 shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
              {features.map((f) => (
                <div
                  key={f.title}
                  className="group bg-gradient-card rounded-2xl p-7 shadow-soft hover:shadow-elegant transition-smooth border border-border/60"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-ocean text-primary-foreground flex items-center justify-center shadow-glow group-hover:scale-110 transition-smooth">
                    <f.icon className="w-5 h-5" />
                  </div>
                  <h3 className="font-display text-xl font-semibold mt-5">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DESTINATIONS */}
      <section className="container mx-auto px-4 py-24">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.25em] text-primary font-semibold">
              Destinations
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
              Postcards from the Dominican Republic.
            </h2>
          </div>
          <Link
            to="/destinations"
            className="group inline-flex items-center gap-2 text-primary font-semibold"
          >
            View all destinations
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {destinations.map((d, idx) => (
            <Link
              key={d.name}
              to="/destinations"
              className={`group relative overflow-hidden rounded-3xl shadow-card hover:shadow-elegant transition-smooth ${
                idx === 0 ? "md:row-span-2 md:aspect-auto" : ""
              }`}
              style={idx === 0 ? { minHeight: "100%" } : undefined}
            >
              <div className={idx === 0 ? "h-full min-h-[28rem]" : "aspect-[4/5]"}>
                <img
                  src={d.image}
                  alt={d.name}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-105 transition-smooth duration-700"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-[hsl(var(--hero-bg))]/90 via-[hsl(var(--hero-bg))]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-white">
                <div className="flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-primary-glow">
                  <MapPin className="w-3.5 h-3.5" />
                  {d.region}
                </div>
                <h3 className="font-display text-2xl md:text-3xl font-semibold mt-2">
                  {d.name}
                </h3>
                <p className="mt-1.5 text-sm text-white/80 max-w-xs">{d.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-hero -z-10" />
        <div className="absolute inset-0 -z-10 opacity-20">
          <img src={heroImg} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>

        <div className="container mx-auto px-4 text-white">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs uppercase tracking-[0.25em] text-primary-glow font-semibold">
              Travelers' words
            </span>
            <h2 className="font-display text-4xl md:text-5xl font-semibold mt-3 leading-tight">
              Five-star moments, again and again.
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t) => (
              <div
                key={t.name}
                className="glass-dark rounded-3xl p-7 shadow-elegant hover:-translate-y-1 transition-smooth"
              >
                <Quote className="w-8 h-8 text-primary-glow mb-4" />
                <p className="text-white/90 leading-relaxed">{t.text}</p>
                <div className="mt-6 flex items-center justify-between">
                  <div>
                    <div className="font-display font-semibold">{t.name}</div>
                    <div className="text-xs text-white/60 mt-0.5">{t.place}</div>
                  </div>
                  <div className="flex gap-0.5 text-accent">
                    {Array.from({ length: t.rating }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-current" />
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="container mx-auto px-4 py-24">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-ocean p-10 md:p-16 text-primary-foreground shadow-elegant">
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-accent/30 blur-3xl" />
          <div className="relative grid md:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-display text-4xl md:text-5xl font-semibold leading-tight">
                Ready when you are.
              </h2>
              <p className="mt-4 text-white/85 text-lg max-w-md">
                Tell us where, when, and how many. We'll handle the rest — from the moment you land to the last sunset.
              </p>
            </div>
            <div className="flex md:justify-end gap-3 flex-wrap">
              <Link
                to="/transfers"
                className="px-7 py-3.5 rounded-full bg-white text-primary font-semibold shadow-glow hover:-translate-y-0.5 transition-smooth"
              >
                Book a transfer
              </Link>
              <Link
                to="/tours"
                className="px-7 py-3.5 rounded-full border border-white/40 text-white font-semibold hover:bg-white/10 transition-smooth"
              >
                Browse tours
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Index;
